// frontend/src/hooks/useAssets.ts — the register read surface (list · detail).
// Project-scoped server state (FRONTEND-PATTERNS §State); the sites offset
// worked example applied to the register: the query keys on the URL view-state,
// `keepPreviousData` holds the prior page, mutations invalidate the prefix. The
// list's answer is stamped with the FRAME it was asked in (`lib/bands`), and
// `assetsQuestion` is the one question the list, its count and its files ask.
//
// 401 is never touched here — the centralized apiClient owns it (ADR-003).

import type { CountResponse } from '@ems-app/schemas/common/pagination';
import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData, type UseInfiniteQueryResult, type UseQueryResult } from '@tanstack/react-query';
import type { AuditListResponse } from '@ems-app/schemas/audit/responses';
import type {
  AssetAccess,
  AssetBooks,
  AssetDetail,
  AssetFilterOptions,
  AssetHistoryResponse,
  AssetListResponse,
  RevealDeviceSecretResponse,
} from '@ems-app/schemas/assets/responses';
import type { DeviceAccessKind } from '@ems-app/schemas/assets/access';
import type { AssetLookupRow, AssetLookupRule } from '@ems-app/schemas/assets/lookup';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import type { AssetListGroup } from '@ems-app/schemas/assets/requests';
import { useMemo } from 'react';
import type { FacedRow, ParentedServerSource, ParentedSource, ServerSource } from './useOptions.js';
import { useLookup, type LookupSource } from './useLookup.js';
import { apiClient } from '../lib/apiClient.js';
import { framed, type ListFrame } from '../lib/bands.js';

/** The view-state the list query travels on. Arrays serialize to CSV. */
export interface AssetsListParams {
  limit: number;
  offset: number;
  q?: string;
  /** register (ops — default) · books (finance; needs asset.books:read) ·
   *  devices (the Devices entry: capable population implied server-side, the
   *  connectivity block + family counts on the wire — plain asset:read). */
  face?: 'register' | 'books' | 'devices';
  category?: string[];
  status?: string[];
  site_id?: string;
  warehouse_id?: string;
  model_id?: string;
  parent_id?: string;
  /** Allocation lens — 'pool' = Unallocated only · 'project' = own rows only. */
  allocation?: 'pool' | 'project';
  /** Resolved network capability (the Via picker's population). */
  capable?: boolean;
  /** The Location filter's kinded tokens (`site:<id>` · `depot:<id>` · `vehicle:<id>` · `<kind>:*` — ADR-037 §2). */
  location?: string[];
  /** The territory's kinded tokens (`zone:<id>` · `cluster:<id>` — the sites list's own grammar). */
  territory?: string[];
  /** The Devices entry's Connectivity filter group (face=devices only). */
  path?: string[];
  service_kind?: string[];
  credential_state?: string[];
  condition?: string[];
  /** The strip's kind lens (missing · conflict · unverified). */
  queue?: string[];
  /** The family dimension — observation-registry keys (asset.missing_serial …). */
  observation?: string[];
  impact?: string[];
  book_status?: string[];
  acquisition_mode?: string[];
  warranty?: string[];
  owner_id?: string[];
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  group_by?: AssetListGroup;
  /** The grouped view's OPEN set — the opened bands' keys, `*` = every band; absent = folded. */
  open?: string[];
}

/** The units as a picker's source (ADR-080 §3 — the server kind, always inside
 *  ONE site; the face is `useAssetFace()`). */
export function assetSource(
  projectId: string,
  rule: AssetLookupRule,
  face: (row: AssetLookupRow) => FacedRow,
  opts: { site: string; self?: string; placing?: AssetCategory; category?: AssetCategory },
): ParentedServerSource<'assets'> {
  // Every unit rule stands INSIDE a site: a field on this source declares `dependsOn` (ADR-080 §9) — the type asks for it.
  return { kind: 'server', projectId, lookup: { entity: 'assets', params: { rule, site: opts.site, self: opts.self, placing: opts.placing, category: opts.category } }, face, parented: true };
}

/** The units of ONE FIXED place — the page's own site, which cannot change while the page lives (a filter on a site's
 *  Files tab, a picker inside a site's library): the same read as `assetSource`, standing on its own, so no parent
 *  declaration is asked of a host that has no parent field. A FORM whose site is a field keeps `assetSource` + `dependsOn`. */
export function assetSourceAt(
  projectId: string,
  rule: AssetLookupRule,
  face: (row: AssetLookupRow) => FacedRow,
  opts: { site: string; self?: string; category?: AssetCategory },
): ServerSource<'assets'> {
  return { kind: 'server', projectId, lookup: { entity: 'assets', params: { rule, site: opts.site, self: opts.self, category: opts.category } }, face };
}

/** The NAMES of a few units of ONE site by id — the unit lookup's rows of given ids (ADR-078 §6), on the site's own
 *  `at_place` source (the cache the site's pickers write to, so a unit met by typing is named here too). A surface that
 *  shows a file's named units, or an upload's picks once its picker folded, asks "name these codes" — never a list. */
export function useNamedUnits(projectId: string, siteId: string | null, ids: readonly string[]): { byId: ReadonlyMap<string, AssetLookupRow>; naming: boolean; unavailable: ReadonlySet<string> } {
  const source = useMemo<LookupSource<'assets'>>(() => ({ entity: 'assets', params: { rule: 'at_place', site: siteId ?? '' } }), [siteId]);
  const key = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids stand in by their joined key (a caller's fresh array each render)
  const stable = useMemo(() => [...new Set(ids)], [key]);
  const look = useLookup(projectId, source, stable, { open: false, enabled: siteId !== null && stable.length > 0 });
  return { byId: look.known, naming: look.naming, unavailable: look.unavailable };
}

export const assetsKeys = {
  /** List key — also the invalidation PREFIX (covers detail). */
  all: (projectId: string) => ['assets', projectId] as const,
  list: (projectId: string, params: AssetsListParams) => ['assets', projectId, 'list', params] as const,
  detail: (projectId: string, assetId: string) => ['assets', projectId, assetId] as const,
  books: (projectId: string, assetId: string) => ['assets', projectId, assetId, 'books'] as const,
  access: (projectId: string, assetId: string) => ['assets', projectId, assetId, 'access'] as const,
};

// Flatten to axios query params — arrays → CSV (empty omitted), undefined dropped.
function toWireParams(p: AssetsListParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = { limit: p.limit, offset: p.offset };
  if (p.q) out.q = p.q;
  if (p.face) out.face = p.face;
  if (p.site_id) out.site_id = p.site_id;
  if (p.warehouse_id) out.warehouse_id = p.warehouse_id;
  if (p.model_id) out.model_id = p.model_id;
  if (p.parent_id) out.parent_id = p.parent_id;
  if (p.allocation) out.allocation = p.allocation;
  if (p.capable) out.capable = 'true';
  if (p.sort_by) out.sort_by = p.sort_by;
  if (p.sort_dir) out.sort_dir = p.sort_dir;
  if (p.group_by) out.group_by = p.group_by;
  const csv = (k: string, v?: string[]) => {
    if (v && v.length > 0) out[k] = v.join(',');
  };
  csv('category', p.category);
  csv('status', p.status);
  csv('location', p.location);
  csv('territory', p.territory);
  csv('path', p.path);
  csv('service_kind', p.service_kind);
  csv('credential_state', p.credential_state);
  csv('condition', p.condition);
  csv('queue', p.queue);
  csv('observation', p.observation);
  csv('impact', p.impact);
  csv('book_status', p.book_status);
  csv('acquisition_mode', p.acquisition_mode);
  csv('warranty', p.warranty);
  csv('owner_id', p.owner_id);
  // The grouped view's open set (`*` = every band); absent = folded.
  csv('open', p.open);
  return out;
}

/** The list's QUESTION — its own wire params less the page and the bands (`limit` · `offset` · `group_by` · `open`):
 *  which units, on which face, in which order. The list's COUNT and every FILE of the view ask exactly this — one
 *  builder, so the three cannot disagree (a file never re-reads the address bar). */
export function assetsQuestion(params: Omit<AssetsListParams, 'limit' | 'offset'>): Record<string, string> {
  const { limit: _l, offset: _o, group_by: _g, open: _p, ...wire } = toWireParams({ ...params, limit: 1, offset: 0 });
  return Object.fromEntries(Object.entries(wire).map(([key, value]) => [key, String(value)]));
}

/** The list's answer, stamped with the FRAME it was asked in (`lib/bands` — the page renders the answer on screen in
 *  the frame that produced it, never the URL's). */
export type AssetsListAnswer = AssetListResponse & { frame: ListFrame<AssetListGroup> };

/** The register's COUNT alone on the same predicates (ADR-078 §6) — the phone
 *  filter drawer's "Show results (n)", a background question (`quietBusy`). The
 *  band parameters never ride it: the count is Σ every band whatever is opened. */
export async function countAssets(projectId: string, params: Omit<AssetsListParams, 'limit' | 'offset'>, signal?: AbortSignal): Promise<number> {
  const r = await apiClient.get<CountResponse>(`/projects/${projectId}/assets/count`, { params: assetsQuestion(params), signal, quietBusy: true });
  return r.data.total;
}

export function useAssetsList(
  projectId: string,
  params: AssetsListParams,
  opts: { enabled?: boolean } = {},
): UseQueryResult<AssetsListAnswer> {
  return useQuery({
    queryKey: assetsKeys.list(projectId, params),
    // A register is NOT live (ADR-074 §2): fresh when opened and after the person's own act. A fast second question
    // cancels the first (the signal).
    queryFn: async (ctx) => {
      const r = await apiClient.get<AssetListResponse>(`/projects/${projectId}/assets`, { params: toWireParams(params), signal: ctx.signal });
      return framed(params)(r.data);
    },
    placeholderData: keepPreviousData, // hold the prior page while the next is in flight
    enabled: opts.enabled ?? true,
  });
}

async function fetchAssetFilterOptions(projectId: string, signal?: AbortSignal): Promise<AssetFilterOptions> {
  const r = await apiClient.get<AssetFilterOptions>(`/projects/${projectId}/assets/filter-options`, { signal });
  return r.data;
}

/** The products used in the project as the asset lists' Product filter (ADR-080 §3 — a small list, asked whole when the
 *  filter opens or a product is in the link; the filter-options read, one cache entry). Product sits INSIDE Category:
 *  each row carries its category as its parent, and the dimension declares `dependsOn: 'category'` (ADR-080 §9). */
export function assetFilterModelsSource(projectId: string): ParentedSource<AssetFilterOptions> {
  return {
    kind: 'small',
    parented: true,
    queryKey: ['assets', projectId, 'filter-options'],
    fetch: (signal) => fetchAssetFilterOptions(projectId, signal),
    rows: (r) => r.models.map((m) => ({ id: m.id, label: m.label, pickable: true, group: null, parent: m.category })),
  };
}

/** The books facet's read. `quiet` — a form's Load the latest: a unit gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchAssetBooks(projectId: string, assetId: string, quiet = false): Promise<AssetBooks> {
  return (await apiClient.get<AssetBooks>(`/projects/${projectId}/assets/${assetId}/books`, quiet ? { quietNotFound: true } : undefined)).data;
}

export function useAssetBooks(projectId: string, assetId: string | null, opts: { enabled?: boolean } = {}): UseQueryResult<AssetBooks> {
  return useQuery({
    queryKey: assetsKeys.books(projectId, assetId ?? ''),
    queryFn: () => fetchAssetBooks(projectId, assetId as string),
    enabled: (opts.enabled ?? true) && assetId !== null,
  });
}

/** The LIFECYCLE ledger (asset_events) — the detail page's History section.
 *  Unpaginated by contract (a unit lives tens of events; the response schema
 *  records the choice), server-ordered oldest-first — the section reverses. */
export function useAssetHistory(projectId: string, assetId: string | null): UseQueryResult<AssetHistoryResponse> {
  return useQuery({
    queryKey: ['assets', projectId, assetId ?? '', 'history'],
    queryFn: async () => {
      const r = await apiClient.get<AssetHistoryResponse>(`/projects/${projectId}/assets/${assetId}/history`);
      return r.data;
    },
    enabled: assetId !== null,
  });
}

/** The unit's RECORD CHANGES — its own audit rows under its own read (asset:read
 *  × the unit's visibility, never the project audit key), the places and units
 *  the viewer may not see named by their kind (the naming rule). Cursor pages —
 *  the audit read's own ("load older"). */
export function useAssetChanges(projectId: string, assetId: string, opts: { enabled?: boolean } = {}): UseInfiniteQueryResult<InfiniteData<AuditListResponse>, Error> {
  return useInfiniteQuery({
    queryKey: ['assets', projectId, assetId, 'changes'],
    queryFn: async ({ pageParam }) => {
      const r = await apiClient.get<AuditListResponse>(`/projects/${projectId}/assets/${assetId}/changes`, {
        params: pageParam ? { cursor: pageParam } : {},
      });
      return r.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: opts.enabled ?? true,
  });
}

/** The connectivity facet's read. `quiet` — a form's Load the latest: a unit gone meanwhile answers the form, never
 *  the not-found page (ADR-084 §6). */
export async function fetchAssetAccess(projectId: string, assetId: string, quiet = false): Promise<AssetAccess> {
  return (await apiClient.get<AssetAccess>(`/projects/${projectId}/assets/${assetId}/access`, quiet ? { quietNotFound: true } : undefined)).data;
}

/** The connectivity facet's ADDRESS half (asset:read — the F6 ruling; secrets
 *  come back as markers only). */
export function useAssetAccess(projectId: string, assetId: string | null, opts: { enabled?: boolean } = {}): UseQueryResult<AssetAccess> {
  return useQuery({
    queryKey: assetsKeys.access(projectId, assetId ?? ''),
    queryFn: () => fetchAssetAccess(projectId, assetId as string),
    enabled: (opts.enabled ?? true) && assetId !== null,
  });
}

/** The audited per-field reveal — one sealed value per call (the server
 *  writes `credential_revealed` and brakes 30/min per actor). Never cached. */
export async function revealAssetSecret(
  projectId: string,
  assetId: string,
  kind: DeviceAccessKind,
  field: string,
): Promise<string> {
  const r = await apiClient.post<RevealDeviceSecretResponse>(`/projects/${projectId}/assets/${assetId}/access/reveal`, {
    kind,
    field,
  });
  return r.data.value;
}

/** The unit's detail read. `quiet` — a form's Load the latest: a unit gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchAssetDetail(projectId: string, assetId: string, quiet = false): Promise<AssetDetail> {
  return (await apiClient.get<AssetDetail>(`/projects/${projectId}/assets/${assetId}`, quiet ? { quietNotFound: true } : undefined)).data;
}

export function useAssetDetail(projectId: string, assetId: string | null): UseQueryResult<AssetDetail> {
  return useQuery({
    queryKey: assetsKeys.detail(projectId, assetId ?? ''),
    queryFn: () => fetchAssetDetail(projectId, assetId as string),
    enabled: assetId !== null,
  });
}
